import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Tooltip,
	Typography,
	lighten,
	useMediaQuery,
	useTheme
} from "@mui/material";
import { Delete, Download } from "@mui/icons-material";
import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { FileUploader, MimeTypes, usePopupAlert } from "@sal/onevent-portal";
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { HerculesDocument, useHerculesDocuments } from "../hooks";

interface HerculesDocumentsProps {
    consignmentNumber?: string;
    onDocumentUploaded?: () => void;
}

interface HerculesGridRow extends HerculesDocument {
	rowId: string;
	isLocalUpload?: boolean;
	localFileKey?: string;
}

const apiPathname = process.env.ONEVENT_API_PATHNAME || "";

function herculesUrl(action: string, consignmentNumber: string, extra?: string): string {
	const base = `${apiPathname}/hercules/${action}/${encodeURIComponent(consignmentNumber)}`;
	return extra ? `${base}/${encodeURIComponent(extra)}` : base;
}

function triggerBrowserDownload(blob: Blob, name: string) {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = name;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}

export function HerculesDocuments({ consignmentNumber, onDocumentUploaded }: HerculesDocumentsProps) {
	const theme = useTheme();
	const popup = usePopupAlert();
	const mobileBreakPoint = useMediaQuery(theme.breakpoints.down("md"));
	const { files, isLoading, isFetching, refetchDocuments } = useHerculesDocuments(consignmentNumber);
	const noDocumentMessage = "No document found";
	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const [uploadedFileKeys, setUploadedFileKeys] = useState<Set<string>>(new Set());
	const [selectedFile, setSelectedFile] = useState<HerculesDocument | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const saveMutation = useMutation<void, Error, File[]>({
		mutationFn: async (filesToUpload) => {
			if (!consignmentNumber) {
				throw new Error("Consignment number is required");
			}

			for (const file of filesToUpload) {
				const formData = new FormData();
				formData.append("Document", file, file.name);

				const response = await fetch(
					herculesUrl("UploadDocument", consignmentNumber),
					{ method: "POST", body: formData }
				);

				if (!response.ok) {
					throw new Error("Upload failed.");
				}
			}
		},
		onSuccess: async (_data, uploadedFiles) => {
			setUploadedFileKeys((prev) => {
				const next = new Set(prev);
				for (const file of uploadedFiles) {
					next.add(`${file.name}-${file.size}-${file.lastModified}`);
				}
				return next;
			});
			setPendingFiles([]);
			setUploadedFileKeys(new Set());
			popup({ severity: "success", message: "Document(s) saved successfully" });
			await refetchDocuments();
			onDocumentUploaded?.();
		},
		onError: (saveError) => {
			popup({ severity: "error", message: saveError.message });
		}
	});

	const deleteMutation = useMutation<void, Error, HerculesDocument>({
		mutationFn: async (file) => {
			if (!consignmentNumber) {
				throw new Error("Consignment number is required");
			}

			const response = await fetch(
				herculesUrl("DeleteDocument", consignmentNumber, file.fileName),
				{ method: "DELETE" }
			);

			if (!response.ok) {
				throw new Error("Delete failed.");
			}
		},
		onSuccess: async () => {
			setDeleteDialogOpen(false);
			setSelectedFile(null);
			popup({ severity: "success", message: "Document deleted successfully" });
			await refetchDocuments();
		},
		onError: (deleteError) => {
			popup({ severity: "error", message: deleteError.message });
		}
	});

	const pendingUploadCount = useMemo(
		() => pendingFiles.filter((file) => !uploadedFileKeys.has(`${file.name}-${file.size}-${file.lastModified}`)).length,
		[pendingFiles, uploadedFileKeys]
	);

	const handlePendingFilesChange = (nextFiles: File[]) => {
		setPendingFiles((prev) => {
			const merged = [...prev, ...nextFiles];
			const uniqueByKey = new Map<string, File>();

			for (const file of merged) {
				uniqueByKey.set(`${file.name}-${file.size}-${file.lastModified}`, file);
			}

			return Array.from(uniqueByKey.values());
		});
	};

	const displayFiles = useMemo<HerculesGridRow[]>(() => {
		const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;
		const serverRows = files.map((file) => ({
			...file,
			rowId: `server-${file.type}-${file.fileName}`
		}));

		const localRows = pendingFiles
			.filter((file) => !uploadedFileKeys.has(getFileKey(file)))
			.map((file) => {
				const fileKey = getFileKey(file);
				const lastDot = file.name.lastIndexOf(".");

				return {
					rowId: `local-${fileKey}`,
					fileName: file.name,
					name: file.name,
					extension: lastDot >= 0 ? file.name.substring(lastDot) : "",
					type: "Document" as const,
					binaryData: file,
					isLocalUpload: true,
					localFileKey: fileKey
				};
			});

		return [...serverRows, ...localRows];
	}, [files, pendingFiles, uploadedFileKeys]);

	const handleSaveUploads = () => {
		if (!consignmentNumber) {
			popup({ severity: "warning", message: "Consignment number is required to upload." });
			return;
		}

		const filesToUpload = pendingFiles.filter(
			(file) => !uploadedFileKeys.has(`${file.name}-${file.size}-${file.lastModified}`)
		);

		if (filesToUpload.length === 0) {
			popup({ severity: "warning", message: "Please add at least one new file to save." });
			return;
		}

		saveMutation.mutate(filesToUpload);
	};

	const handleDeleteClick = (file: HerculesGridRow) => {
		if (file.isLocalUpload && file.localFileKey) {
			setPendingFiles((prev) =>
				prev.filter((item) => `${item.name}-${item.size}-${item.lastModified}` !== file.localFileKey)
			);
			setUploadedFileKeys((prev) => {
				if (!prev.has(file.localFileKey!)) {
					return prev;
				}

				const next = new Set(prev);
				next.delete(file.localFileKey!);
				return next;
			});
			return;
		}

		setSelectedFile(file);
		setDeleteDialogOpen(true);
	};

	const handleDeleteDialogClose = () => {
		if (deleteMutation.isLoading) {
			return;
		}

		setDeleteDialogOpen(false);
	};

	const handleDownloadClick = async (file: HerculesGridRow) => {
		try {
			const fallbackName = file.fileName || "download";

			if (file.binaryData) {
				popup({ severity: "info", message: "Downloading..." });
				triggerBrowserDownload(file.binaryData, fallbackName);
				return;
			}

			if (!consignmentNumber) {
				return;
			}

			popup({ severity: "info", message: "Downloading..." });
			const endpointType = file.type === "Photo" ? "GetPhoto" : "GetDocument";
			const response = await fetch(
				herculesUrl(endpointType, consignmentNumber, file.fileName)
			);

			if (!response.ok) {
				throw new Error("Download failed.");
			}

			const binary = await response.blob();
			triggerBrowserDownload(binary, fallbackName);
		} catch (downloadError) {
			popup({
				severity: "error",
				message: downloadError instanceof Error ? downloadError.message : "Download failed"
			});
		}
	};

	if (isLoading || isFetching) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center">
				<CircularProgress />
			</Box>
		);
	}

	const renderActions = (file: HerculesGridRow) => (
		<Box display="flex" alignItems="center" gap={1}>
			<Tooltip title="Download">
				<IconButton size="small" color="primary" onClick={async () => { await handleDownloadClick(file); }}>
					<Download fontSize="small" />
				</IconButton>
			</Tooltip>
			{(file.type === "Document" || file.isLocalUpload) && (
				<Tooltip title="Delete">
					<IconButton size="small" color="error" onClick={() => handleDeleteClick(file)}>
						<Delete fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
		</Box>
	);

	const columns: GridColDef<HerculesGridRow>[] = [
		{
			field: "fileName",
			headerName: "File Name",
			flex: 1,
			minWidth: 240,
			display: "flex"
		},
		{
			field: "type",
			headerName: "Type",
			width: 140,
			display: "flex"
		},
		{
			field: "actions",
			headerName: "Actions",
			width: 120,
			display: "flex",
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<HerculesGridRow>) => renderActions(params.row)
		}
	];

	const desktopView = (
		<Grid container spacing={2} alignItems="stretch">
			<Grid item xs={6} sx={{ display: "flex" }}>
				<Box width="100%" height="100%" display="flex">
					<FileUploader
						onFilesChange={handlePendingFilesChange}
						acceptedFileTypes={[
							MimeTypes.pdf,
							MimeTypes.jpeg,
							MimeTypes.doc,
							MimeTypes.docx,
							MimeTypes.png,
							MimeTypes.csv,
							MimeTypes.xls,
							MimeTypes.xlsx,
							MimeTypes.msg
						]}
						multiple
						boxStyle={{
							backgroundColor: lighten(theme.palette.primary.main, 0.9),
							border: `2px dashed ${lighten(theme.palette.primary.main, 0.7)}`,
							borderRadius: "5px",
							padding: "20px",
							boxSizing: "border-box",
							height: "100%"
						}}
						customDropzoneContent={
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: "8px"
								}}
							>
								<i className="fa fa-file-upload" style={{ fontSize: "40px", color: theme.palette.primary.main }} />
								<Typography
									variant="body1"
									sx={{ color: theme.palette.common.black, fontWeight: "500", fontSize: "1rem" }}
								>
									Drag & drop files or <span style={{ color: theme.palette.primary.main, fontWeight: "bold" }}>Browse</span>
								</Typography>
								<Typography variant="caption" sx={{ color: "#bdbdbd", fontSize: "0.75rem" }} />
							</Box>
						}
						dropzoneStyles={{
							backgroundColor: lighten(theme.palette.primary.main, 0.9),
							border: "none"
						}}
					/>
				</Box>
			</Grid>
			<Grid item xs={6}>
				<DataGridPro<HerculesGridRow>
					rows={displayFiles}
					columns={columns}
					getRowId={(row) => row.rowId}
					loading={isLoading || isFetching}
					rowSelection={false}
					hideFooter
					autoHeight
					localeText={{
						noRowsLabel: noDocumentMessage
					}}
					sx={{
						minHeight: "150px",
						"& .MuiDataGrid-columnHeaders": {
							position: "relative",
							zIndex: 1,
							"&::before": {
								content: "\"\"",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: theme.palette.common.white,
								zIndex: -1
							}
						},
						"& .MuiDataGrid-columnHeader, & .MuiDataGrid-scrollbarFiller": {
							backgroundColor: `${theme.palette.common.white} !important`
						},
						"& .MuiDataGrid-columnHeader": {
							color: theme.palette.common.black,
							fontWeight: 600
						},
						"& .MuiDataGrid-columnHeaderTitle": {
							color: theme.palette.common.black,
							fontWeight: 600,
							fontSize: "0.9rem"
						},
						"& .MuiDataGrid-cell": {
							py: "12px",
							px: "8px",
							alignItems: "center"
						},
						"& .MuiInputBase-root": {
							fontSize: "0.85rem"
						},
						"& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
							borderBottom: "1px solid #e0e0e0"
						},
						"& .MuiDataGrid-footerContainer": {
							display: "none"
						}
					}}
				/>
			</Grid>
		</Grid>
	);

	const mobileView = (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Box width="100%" height="100%">
					<FileUploader
						onFilesChange={handlePendingFilesChange}
						acceptedFileTypes={[
							MimeTypes.pdf,
							MimeTypes.jpeg,
							MimeTypes.doc,
							MimeTypes.docx,
							MimeTypes.png,
							MimeTypes.csv,
							MimeTypes.xls,
							MimeTypes.xlsx,
							MimeTypes.msg
						]}
						multiple
						boxStyle={{
							backgroundColor: lighten(theme.palette.primary.main, 0.9),
							border: `2px dashed ${lighten(theme.palette.primary.main, 0.7)}`,
							borderRadius: "5px",
							padding: "20px",
							boxSizing: "border-box"
						}}
						dropzoneStyles={{
							backgroundColor: lighten(theme.palette.primary.main, 0.9),
							border: "none"
						}}
					/>
				</Box>
			</Grid>
			<Grid item xs={12}>
				{displayFiles.length === 0 ? (
					<Typography align="center" color="textSecondary">
						{noDocumentMessage}
					</Typography>
				) : (
					displayFiles.map((file) => (
						<Card key={file.rowId} variant="outlined" sx={{ mb: 1 }}>
							<CardContent>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs>
										<Typography variant="subtitle2" fontWeight={600}>
											{file.fileName}
										</Typography>
										<Typography variant="caption" color="textSecondary" textTransform="capitalize">
											{file.type}
										</Typography>
									</Grid>
									<Grid item xs="auto">
										{renderActions(file)}
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					))
				)}
			</Grid>
		</Grid>
	);

	return (
		<Box>
			{mobileBreakPoint ? mobileView : desktopView}
			<Box display="flex" justifyContent="flex-end" mt={2}>
				<Button
					variant="contained"
					color="primary"
					onClick={handleSaveUploads}
					disabled={!consignmentNumber || saveMutation.isLoading || pendingUploadCount === 0}
				>
					{saveMutation.isLoading ? "Saving..." : "Save"}
				</Button>
			</Box>

			<Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} disableEscapeKeyDown={deleteMutation.isLoading}>
				<DialogTitle>Delete Document</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete &quot;{selectedFile?.fileName}&quot;? This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteDialogClose} disabled={deleteMutation.isLoading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={() => selectedFile && deleteMutation.mutate(selectedFile)}
						disabled={deleteMutation.isLoading}
					>
						{deleteMutation.isLoading ? <CircularProgress size={20} /> : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
